// Right-rail panel shown while an expansion study zone is active.
// Replaces BomPanel / InspectorPanel in the Builder right column.
// Implements the "money story" for private-capex zones:
//   - big headline: payback months
//   - passings split (served vs BEAD-eligible vs unknown)
//   - incremental capex + $/passing
//   - revenue inputs as sliders (ARPU, take rate, horizon)
//   - 5-yr contribution margin tile
//   - Commit / Discard buttons

import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { toast } from '../lib/toast';

export type ExpansionZone = {
  id: string;
  project_id: string;
  plant_id: string | null;
  name: string;
  zone_type: 'contiguous' | 'backhaul_fiber' | 'backhaul_wireless';
  status: 'draft' | 'analyzed' | 'committed' | 'rejected';
  selection_mode: string;
  zone_geojson: any;
  backhaul_geojson?: any;
  wireless_capex?: number;
  revenue_assumptions: {
    arpu_month: number; take_rate: number; horizon_months: number;
    gm_pct: number; discount_rate: number;
  };
  analysis: any;
  created_at?: string;
  committed_at?: string | null;
  territory_plan_id?: string | null;
  territory_plan_name?: string | null;
};

const fmtUSD = (n: number) =>
  n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(2)}M`
  : n >= 1_000   ? `$${(n / 1_000).toFixed(0)}k`
  : `$${Math.round(n).toLocaleString()}`;
const fmtNum = (n: number) => Math.round(n).toLocaleString();
const fmtMonths = (n: number | null) =>
  n == null || !isFinite(n) ? '—' : n < 12 ? `${n.toFixed(1)} mo` : `${(n / 12).toFixed(1)} yr`;

export default function ExpansionStudyPanel({
  zone,
  onClose,
  onCommitted,
  onRejected,
  onBackhaulSelect,
}: {
  zone: ExpansionZone;
  onClose: () => void;
  onCommitted: (z: ExpansionZone) => void;
  onRejected: (z: ExpansionZone) => void;
  onBackhaulSelect?: (option: string | null, hops?: any[]) => void;
}) {
  const qc = useQueryClient();
  const analyzed = zone.status === 'analyzed' || zone.status === 'committed';
  const a = zone.analysis ?? {};
  const passings = a.passings ?? { total: 0, served: 0, bead_eligible: 0, unknown: 0 };

  // Editable revenue assumptions (client-side until analyze fires)
  const [rev, setRev] = useState(zone.revenue_assumptions);

  // Auto-emit backhaul selection when analysis arrives (not just on click).
  // This ensures the map renders the correct path immediately.
  useEffect(() => {
    if (analyzed && a.backhaul_options) {
      const rec = a.backhaul_options.recommended ?? 'build_fiber';
      const wirelessData = a.backhaul_options.wireless_ptp;
      onBackhaulSelect?.(rec, wirelessData?.hops ?? null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analyzed, a.backhaul_options?.recommended]);

  const analyzeMut = useMutation({
    mutationFn: () => api.post(`/expansion/zones/${zone.id}/analyze`, {
      revenue_assumptions: rev,
    }),
    onSuccess: (res: any) => {
      toast.success(`Extension design ran — ${fmtNum(res.analysis.passings.total)} passings, ${fmtUSD(res.analysis.incremental_capex)} capex`);
      qc.invalidateQueries({ queryKey: ['expansion-zone', zone.id] });
      qc.invalidateQueries({ queryKey: ['expansion-zones', zone.project_id] });
      qc.invalidateQueries({ queryKey: ['expansion-design', zone.id] });
    },
    onError: (e: any) => toast.error(`Analyze failed: ${e?.message ?? e}`),
  });

  const commitMut = useMutation({
    mutationFn: () => api.post(`/expansion/zones/${zone.id}/commit`, {}),
    onSuccess: () => {
      toast.success(`${zone.name} saved as project. Visible on BEAD Report.`);
      qc.invalidateQueries({ queryKey: ['expansion-zones', zone.project_id] });
      qc.invalidateQueries({ queryKey: ['plant-graph'] });
      qc.invalidateQueries({ queryKey: ['bom'] });
      onCommitted({ ...zone, status: 'committed' });
    },
    onError: (e: any) => toast.error(`Commit failed: ${e?.message ?? e}`),
  });

  const rejectMut = useMutation({
    mutationFn: () => api.post(`/expansion/zones/${zone.id}/reject`, {}),
    onSuccess: () => {
      toast.info(`${zone.name} discarded.`);
      qc.invalidateQueries({ queryKey: ['expansion-zones', zone.project_id] });
      onRejected({ ...zone, status: 'rejected' });
    },
  });

  const sellable = passings.served + passings.bead_eligible + passings.unknown;
  const paybackMonths: number | null = a.simple_payback_months ?? null;
  const paybackColor =
    paybackMonths == null ? '#64748b' :
    paybackMonths < 24  ? '#10b981' :
    paybackMonths < 48  ? '#f59e0b' :
    '#ef4444';

  return (
    <div className="expansion-panel">
      <div className="expansion-panel__hd">
        <div>
          <div style={{ fontSize: 10, color: '#0ea5e9', fontWeight: 700, letterSpacing: '.08em' }}>
            EXPANSION STUDY
          </div>
          <div style={{ fontWeight: 700, fontSize: 15 }}>{zone.name}</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            {zone.zone_type === 'contiguous' ? 'Contiguous extension' :
             zone.zone_type === 'backhaul_fiber' ? 'Fiber P2P backhaul' :
             'Wireless P2P backhaul'}
            {' · '}
            {zone.status === 'draft' ? 'Draft — not yet analyzed' :
             zone.status === 'analyzed' ? 'Analyzed' :
             zone.status === 'committed' ? 'Saved as Project' :
             'Rejected'}
          </div>
        </div>
        <button
          className="btn"
          onClick={onClose}
          style={{ padding: '3px 8px', fontSize: 12 }}
        >close</button>
      </div>

      {/* HEADLINE — payback or status */}
      {!analyzed ? (
        <div className="expansion-headline expansion-headline--draft">
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
            Zone drawn. Click "Run analysis" to design the extension plant and compute feasibility.
          </div>
          <button
            className="btn btn--primary"
            onClick={() => analyzeMut.mutate()}
            disabled={analyzeMut.isPending}
            style={{ width: '100%' }}
          >
            {analyzeMut.isPending ? 'Designing extension… (5-15s)' : '▶ Run analysis'}
          </button>
        </div>
      ) : (
        <div className="expansion-headline" style={{ borderColor: paybackColor }}>
          <div className="expansion-headline__big" style={{ color: paybackColor }}>
            {fmtMonths(paybackMonths)}
          </div>
          <div className="expansion-headline__label">Simple payback</div>
          <div className="expansion-headline__sub">
            {fmtUSD(a.incremental_capex)} capex · {fmtUSD(a.gm_monthly ?? 0)}/mo gross margin at {(rev.take_rate * 100).toFixed(0)}% take
          </div>
        </div>
      )}

      {/* BACKHAUL OPTIONS — first decision point, before economics */}
      {analyzed && a.backhaul_options && (
        <BackhaulOptions
          options={a.backhaul_options}
          onSelect={(type, hops) => onBackhaulSelect?.(type, hops)}
        />
      )}

      {/* PASSINGS SPLIT */}
      {analyzed && (
        <div className="expansion-section">
          <div className="expansion-section__h">Passings in zone</div>
          <PassingsBars p={passings} />
          <div style={{ fontSize: 11, color: 'var(--text-subtle)', marginTop: 6 }}>
            {sellable > 0
              ? `${fmtNum(sellable)} addressable · grant-ineligible served homes count most — pure private ROI.`
              : 'No locations found inside zone.'}
          </div>
          {(a.along_route_sellable ?? 0) > 0 && (
            <div style={{
              marginTop: 10, padding: 8, background: '#eff6ff', border: '1px solid #93c5fd',
              borderRadius: 6, fontSize: 12,
            }}>
              <div style={{ fontWeight: 700, color: '#1e40af' }}>
                + {fmtNum(a.along_route_sellable)} bonus passings along backhaul
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                Homes within 300 ft of the highway run — same capex, extra revenue.
                {a.passings?.along_route_served ? ` (${fmtNum(a.passings.along_route_served)} served, ${fmtNum(a.passings.along_route_unknown ?? 0)} addressable)` : ''}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ECONOMICS */}
      {analyzed && (
        <div className="expansion-section">
          <div className="expansion-section__h">Economics</div>
          <div className="expansion-tiles">
            <Tile label="Incremental capex" value={fmtUSD(a.incremental_capex)} />
            <Tile label="$ / passing" value={fmtUSD(a.incremental_per_passing)}
                  tone={a.incremental_per_passing < 1500 ? 'green' : 'amber'} />
            <Tile label="MRR uplift" value={fmtUSD(a.mrr_uplift) + '/mo'} />
            <Tile label={`${rev.horizon_months}-mo contribution`} value={fmtUSD(a.contribution_horizon)}
                  tone="green" />
          </div>
        </div>
      )}

      {/* REVENUE SLIDERS */}
      <div className="expansion-section">
        <div className="expansion-section__h">Revenue assumptions</div>
        <Slider
          label="ARPU ($/mo)" min={40} max={200} step={5}
          value={rev.arpu_month}
          onChange={(v) => setRev((r) => ({ ...r, arpu_month: v }))}
          suffix="/mo"
        />
        <Slider
          label="Take rate" min={0.1} max={0.8} step={0.05}
          value={rev.take_rate}
          onChange={(v) => setRev((r) => ({ ...r, take_rate: v }))}
          suffix={(v) => `${(v * 100).toFixed(0)}%`}
        />
        <Slider
          label="Horizon (mo)" min={24} max={120} step={6}
          value={rev.horizon_months}
          onChange={(v) => setRev((r) => ({ ...r, horizon_months: v }))}
          suffix=" mo"
        />
        {analyzed && (
          <button
            className="btn"
            onClick={() => analyzeMut.mutate()}
            disabled={analyzeMut.isPending}
            style={{ width: '100%', marginTop: 4, fontSize: 12 }}
          >
            {analyzeMut.isPending ? 'Recomputing…' : 'Recompute with new assumptions'}
          </button>
        )}
      </div>

      {/* EXTENSION PLANT DETAIL */}
      {analyzed && (
        <div className="expansion-section">
          <div className="expansion-section__h">Cost breakdown</div>
          <MiniRow k="Feeder" v={`${fmtNum(a.feeder_ft ?? 0)} ft`} />
          <MiniRow k="Distribution" v={`${fmtNum(a.distribution_ft ?? 0)} ft`} />
          <MiniRow k="Drops" v={`${fmtNum(a.drop_ft ?? 0)} ft`} />
          <MiniRow k="Cable $" v={fmtUSD(a.cable_cost ?? 0)} />
          <MiniRow k="Hardware $" v={fmtUSD(a.hardware_cost ?? 0)} />
          <MiniRow k="Labor $" v={fmtUSD(a.labor_cost ?? 0)} />
          <MiniRow k="Adders $" v={fmtUSD(a.adders_cost ?? 0)} />
          <MiniRow k="— In-zone total —" v={fmtUSD((a.cable_cost ?? 0) + (a.hardware_cost ?? 0) + (a.labor_cost ?? 0) + (a.adders_cost ?? 0))} tone="strong" />
        </div>
      )}

      {/* DOWNLOADS — KMZ + Zip package */}
      {analyzed && (
        <div className="panel-section">
          <div className="panel-section__label">Export</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <a
              href={`/api/expansion/zones/${zone.id}/package.kmz`}
              className="btn"
              style={{ flex: 1, textAlign: 'center', textDecoration: 'none', background: '#e0f2fe', color: '#0369a1', borderColor: '#7dd3fc' }}
              download
              title="Google Earth-compatible KMZ of the zone boundary, fiber segments, and nodes"
            >
              ⬇ KMZ (Google Earth)
            </a>
            <a
              href={`/api/expansion/zones/${zone.id}/package.zip`}
              className="btn btn--primary"
              style={{ flex: 1, textAlign: 'center', textDecoration: 'none' }}
              download
              title="Full package: KMZ + BoM CSV + summary + GeoJSON"
            >
              ⬇ Shovel-ready package
            </a>
          </div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>
            KMZ opens in Google Earth / QGIS. Zip includes BoM CSV, summary, and GeoJSON.
          </div>
        </div>
      )}

      {/* COMMIT */}
      {analyzed && zone.status !== 'committed' && (
        <div className="expansion-footer">
          <button
            className="btn btn--primary"
            onClick={() => commitMut.mutate()}
            disabled={commitMut.isPending}
            style={{ flex: 1 }}
          >
            {commitMut.isPending ? 'Saving…' : '✓ Save as Project'}
          </button>
          <button
            className="btn"
            onClick={() => rejectMut.mutate()}
            disabled={rejectMut.isPending}
            style={{ color: '#dc2626', borderColor: '#fca5a5' }}
          >
            Discard
          </button>
        </div>
      )}
      {zone.status === 'committed' && (
        <div className="expansion-footer" style={{ color: '#10b981', fontWeight: 600, justifyContent: 'center' }}>
          ✓ Saved — visible as a project on BEAD Report
        </div>
      )}
    </div>
  );
}

// ------- Sub-components -------

function Tile({ label, value, tone }: { label: string; value: string; tone?: 'green' | 'amber' }) {
  const color = tone === 'green' ? '#10b981' : tone === 'amber' ? '#f59e0b' : 'var(--text)';
  return (
    <div className="expansion-tile">
      <div className="expansion-tile__label">{label}</div>
      <div className="expansion-tile__value" style={{ color }}>{value}</div>
    </div>
  );
}

function MiniRow({ k, v, tone }: { k: string; v: string; tone?: 'strong' | 'amber' | 'savings' }) {
  const weight = tone === 'strong' ? 700 : 400;
  const color =
    tone === 'amber'   ? '#f59e0b' :
    tone === 'savings' ? '#10b981' :
    'var(--text)';
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 12, fontWeight: weight, color }}>
      <span style={{ color: tone === 'strong' ? 'var(--text)' : 'var(--text-muted)' }}>{k}</span>
      <span>{v}</span>
    </div>
  );
}

function Slider({
  label, min, max, step, value, onChange, suffix,
}: {
  label: string; min: number; max: number; step: number;
  value: number; onChange: (v: number) => void;
  suffix?: string | ((v: number) => string);
}) {
  const sfx = typeof suffix === 'function' ? suffix(value) : (suffix ?? '');
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>
        <span>{label}</span>
        <b style={{ color: 'var(--text)' }}>
          {typeof suffix === 'function' ? sfx : `${value}${sfx}`}
        </b>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ width: '100%' }}
      />
    </div>
  );
}

function PassingsBars({ p }: { p: { total: number; served: number; bead_eligible: number; unknown: number } }) {
  const total = p.total || 1;
  const servedPct = (p.served / total) * 100;
  const beadPct = (p.bead_eligible / total) * 100;
  const unkPct = (p.unknown / total) * 100;
  return (
    <div>
      <div style={{ height: 14, display: 'flex', borderRadius: 4, overflow: 'hidden', background: 'var(--border)' }}>
        <div style={{ width: `${servedPct}%`, background: '#10b981' }} title={`Served (private $): ${p.served}`} />
        <div style={{ width: `${beadPct}%`, background: '#0ea5e9' }} title={`BEAD-eligible: ${p.bead_eligible}`} />
        <div style={{ width: `${unkPct}%`, background: '#f59e0b' }} title={`Unknown/addressable: ${p.unknown}`} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, marginTop: 6, fontSize: 11 }}>
        <PassLegend label="Served" count={p.served} color="#10b981" sub="private $" />
        <PassLegend label="BEAD" count={p.bead_eligible} color="#0ea5e9" sub="grant-eligible" />
        <PassLegend label="Unknown" count={p.unknown} color="#f59e0b" sub="addressable" />
      </div>
      <div style={{ marginTop: 4, fontSize: 11, color: 'var(--text-muted)' }}>
        Total passings in zone: <b style={{ color: 'var(--text)' }}>{fmtNum(p.total)}</b>
      </div>
    </div>
  );
}

function PassLegend({ label, count, color, sub }: { label: string; count: number; color: string; sub: string }) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <span style={{ width: 8, height: 8, borderRadius: 2, background: color }} />
        <span style={{ color: 'var(--text-muted)' }}>{label}</span>
      </div>
      <div style={{ fontWeight: 700 }}>{fmtNum(count)}</div>
      <div style={{ fontSize: 10, color: 'var(--text-subtle)' }}>{sub}</div>
    </div>
  );
}

// ------- Backhaul options comparison -------

function BackhaulOptions({ options, onSelect }: { options: any; onSelect?: (type: string, hops?: any[]) => void }) {
  const [selected, setSelected] = useState<string>(options.recommended ?? 'build_fiber');
  const rows = [
    { key: 'hybrid', data: options.hybrid, color: '#f97316', icon: '⚡' },
    { key: 'build_fiber', data: options.build_fiber, color: '#0ea5e9', icon: '🔧' },
    { key: 'wireless_ptp', data: options.wireless_ptp, color: '#dc2626', icon: '📡' },
    { key: 'lease_ptp_fiber', data: options.lease_fiber ?? options.lease_ptp_fiber, color: '#8b5cf6', icon: '📄' },
  ].filter((r) => r.data);

  const recommended = options.recommended;

  const handleSelect = (key: string) => {
    setSelected(key);
    const data = key === 'build_fiber' ? options.build_fiber
      : key === 'wireless_ptp' ? options.wireless_ptp
      : options.lease_fiber ?? options.lease_ptp_fiber;
    onSelect?.(key, data?.hops);
  };

  return (
    <div className="expansion-section">
      <div className="expansion-section__h">Backhaul options</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {rows.map(({ key, data, color, icon }) => {
          const isRec = key === recommended;
          const isSel = key === selected;
          const viable = data.viable;
          return (
            <div
              key={key}
              onClick={() => viable && handleSelect(key)}
              style={{
                padding: 10,
                borderRadius: 8,
                border: `2px solid ${isSel ? color : viable ? 'var(--border)' : '#e5e7eb'}`,
                background: isSel ? `${color}10` : viable ? '#fff' : '#f9fafb',
                opacity: viable ? 1 : 0.5,
                position: 'relative',
                cursor: viable ? 'pointer' : 'not-allowed',
                transition: 'border-color 0.15s, background 0.15s',
              }}
            >
              {isRec && (
                <span style={{
                  position: 'absolute', top: -8, right: 8,
                  background: color, color: '#fff',
                  fontSize: 9, fontWeight: 700, padding: '2px 6px',
                  borderRadius: 4, textTransform: 'uppercase', letterSpacing: '.05em',
                }}>
                  Recommended
                </span>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700 }}>
                    {icon} {data.label}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                    {data.distance_mi > 0 ? `${data.distance_mi} mi` : ''}
                    {data.capacity_label ? ` · ${data.capacity_label}` : ''}
                  </div>
                  {!viable && data.reason && (
                    <div style={{ fontSize: 10, color: '#dc2626', marginTop: 2 }}>{data.reason}</div>
                  )}
                  {data.verdict && (
                    <div style={{ fontSize: 10, marginTop: 2 }}>
                      LoS: <span style={{ color: data.verdict === 'green' ? '#10b981' : data.verdict === 'yellow' ? '#f59e0b' : '#ef4444', fontWeight: 700 }}>
                        {data.verdict === 'green' ? '✓ Clear' : data.verdict === 'yellow' ? '⚠ Marginal' : '✗ Blocked'}
                      </span>
                      {data.fresnel_pct != null && ` · Fresnel ${data.fresnel_pct}%`}
                    </div>
                  )}
                  {data.ride_along_ft > 0 && (
                    <div style={{ fontSize: 10, color: '#10b981', marginTop: 2 }}>
                      {fmtNum(data.ride_along_ft)} ft ride-along saves {fmtUSD(data.ride_along_savings)}
                    </div>
                  )}
                </div>
                <div style={{ textAlign: 'right', minWidth: 80 }}>
                  <div style={{ fontSize: 14, fontWeight: 800, color }}>
                    {fmtUSD(data.total_5yr)}
                  </div>
                  <div style={{ fontSize: 9, color: 'var(--text-muted)' }}>5-yr TCO</div>
                  {data.capex > 0 && (
                    <div style={{ fontSize: 10, color: 'var(--text-subtle)' }}>{fmtUSD(data.capex)} capex</div>
                  )}
                  {data.opex_monthly > 0 && (
                    <div style={{ fontSize: 10, color: 'var(--text-subtle)' }}>{fmtUSD(data.opex_monthly)}/mo</div>
                  )}
                </div>
              </div>
              {/* Tower rental */}
              {data.tower_rental_monthly > 0 && (
                <div style={{ fontSize: 10, color: 'var(--text-subtle)', marginTop: 4 }}>
                  Tower rental: {data.tower_sites} site(s) · {fmtUSD(data.tower_rental_monthly)}/mo
                </div>
              )}
              {/* Single-hop radio detail */}
              {data.radio && !data.hops?.length && (
                <div style={{ fontSize: 10, color: 'var(--text-subtle)', marginTop: 4, borderTop: '1px solid var(--border)', paddingTop: 4 }}>
                  Radio: {data.radio.name} · {fmtUSD(data.radio.price)}/link
                </div>
              )}
              {/* Hybrid per-segment breakdown */}
              {data.segments?.length > 0 && (
                <div style={{ fontSize: 10, color: 'var(--text-subtle)', marginTop: 4, borderTop: '1px solid var(--border)', paddingTop: 4 }}>
                  {data.segments.map((seg: any, i: number) => {
                    const modeColor = seg.mode === 'wireless_ptp' ? '#dc2626' : seg.mode === 'ride_along' ? '#10b981' : '#0ea5e9';
                    const modeIcon = seg.mode === 'wireless_ptp' ? '📡' : seg.mode === 'ride_along' ? '✓' : '🔧';
                    return (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', borderBottom: '1px solid var(--border)' }}>
                        <span>
                          <span style={{ color: modeColor }}>{modeIcon}</span>
                          {' '}Mi {seg.from_mile?.toFixed(1)}–{seg.to_mile?.toFixed(1)}: {seg.mode === 'wireless_ptp' ? 'wireless' : seg.mode === 'ride_along' ? 'ride-along' : 'fiber'}
                          {seg.tower_a && seg.tower_b && <span style={{ color: 'var(--text-muted)' }}> ({seg.tower_a.city}→{seg.tower_b.city})</span>}
                          {seg.throughput_mbps && <span style={{ color: modeColor }}> {seg.throughput_mbps} Mbps</span>}
                        </span>
                        <span style={{ fontWeight: 600 }}>{seg.capex > 0 ? fmtUSD(seg.capex) : seg.opex_monthly > 0 ? `${fmtUSD(seg.opex_monthly)}/mo` : 'free'}</span>
                      </div>
                    );
                  })}
                  {data.summary && <div style={{ marginTop: 4, fontSize: 9, color: 'var(--text-muted)' }}>{data.summary}</div>}
                </div>
              )}
              {/* Multi-hop relay chain detail */}
              {data.hops?.length > 0 && !data.segments?.length && (
                <div style={{ fontSize: 10, color: 'var(--text-subtle)', marginTop: 4, borderTop: '1px solid var(--border)', paddingTop: 4 }}>
                  {data.hops.map((hop: any, i: number) => (
                    <div key={i} style={{ padding: '3px 0', borderBottom: '1px solid var(--border)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>
                          <b>Hop {i + 1}:</b> {hop.from.city} → {hop.to.city}
                          <span style={{ color: hop.verdict === 'green' ? '#10b981' : '#f59e0b', marginLeft: 4 }}>
                            {hop.verdict === 'green' ? '✓ Clear' : '⚠ Marginal'}
                          </span>
                        </span>
                        <span style={{ fontWeight: 600 }}>{hop.throughput_mbps} Mbps</span>
                      </div>
                      <div style={{ fontSize: 9, color: 'var(--text-subtle)' }}>
                        {hop.distance_mi} mi · {hop.radio.name} ({fmtUSD(hop.radio.price)})
                        · Fresnel {hop.fresnel_pct}%
                        {hop.from.agl_m && ` · Towers: ${Math.round(hop.from.agl_m)}m / ${Math.round(hop.to.agl_m)}m AGL`}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {data.note && (
                <div style={{ fontSize: 9, color: 'var(--text-subtle)', marginTop: 3, fontStyle: 'italic' }}>{data.note}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
